import React, { useEffect, useMemo, ReactNode, JSX, useRef } from 'react';
import {
  Select,
  SelectItem,
  Selection,
  SelectedItems,
} from '@nextui-org/react';
import { Settlement, MarketData } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface FleetFilterProps {
  data: Settlement[] | MarketData[];
  selectedFleet: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectedKeys?: string[];
  defaultSelectAll?: boolean;
  hideAllOption?: boolean;
}

const FleetFilter: React.FC<FleetFilterProps> = ({
  data,
  selectedFleet,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectedKeys,
  defaultSelectAll = false,
  hideAllOption = false,
}) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tMarket } = useTranslation('market');
  const finalLabel = label || tCommon('Tipo de Flete');

  // Track the previous fleets to detect changes
  const prevFleetsRef = useRef<string[]>([]);

  const fleets = useMemo(() => {
    // Create a map to count records per fleet type (using codes, not translations)
    const fleetCount = data.reduce((acc, item) => {
      let fleetCode = 'OCEAN'; // Default

      // Handle Settlement type (ship_trip?.ship?.type)
      if ('ship_trip' in item) {
        const shipType = item.ship_trip?.ship?.type;
        if (shipType === 'A') {
          fleetCode = 'AIR';
        } else if (shipType === 'T') {
          fleetCode = 'GROUND';
        } else if (shipType === 'M') {
          fleetCode = 'OCEAN';
        }
      }
      // Handle MarketData type (transport: 'AIR' | 'OCEAN')
      else if ('transport' in item) {
        fleetCode = item.transport;
      }

      acc[fleetCode] = (acc[fleetCode] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Convert to array and sort
    return Object.entries(fleetCount)
      .sort(([, countA], [, countB]) => countB - countA)
      .map(([fleet]) => fleet);
  }, [data]);

  useEffect(() => {
    // Only select all when fleets have changed (e.g., data changed)
    const fleetsChanged =
      fleets.length !== prevFleetsRef.current.length ||
      fleets.some((f) => !prevFleetsRef.current.includes(f));

    // Store current fleets for next comparison
    prevFleetsRef.current = [...fleets];

    if (
      selectionMode === 'multiple' &&
      defaultSelectAll &&
      fleets.length > 0 &&
      fleetsChanged
    ) {
      onSelectionChange(fleets);
    }
  }, [fleets, defaultSelectAll, selectionMode, onSelectionChange]);

  if (fleets.length === 0) {
    return null;
  }

  const handleSelectionChange = (keys: Selection) => {
    if (selectionMode === 'multiple') {
      const keysArray = Array.from(keys);

      if (keysArray.includes('all')) {
        // If 'all' is selected and there were previous selections, clear all
        if (Array.isArray(selectedFleet) && selectedFleet.length > 0) {
          onSelectionChange([]);
        } else {
          // Otherwise, select all fleets
          onSelectionChange(fleets);
        }
      } else {
        // Normal selection behavior for individual items
        onSelectionChange(keysArray as string[]);
      }
    } else {
      // Single selection behavior
      const selectedKeys = Array.from(keys);
      onSelectionChange(selectedKeys as string[]);
    }
  };

  // Create the SelectItem elements
  const selectItemElements: JSX.Element[] = [];

  // Add "all" option for single selection mode
  if (selectionMode === 'single' && !hideAllOption) {
    selectItemElements.push(
      <SelectItem key='all' value='all' textValue={tCommon('Todos')}>
        {tCommon('Todos')}
      </SelectItem>
    );
  }

  // Add "all" option for multiple selection mode
  if (selectionMode === 'multiple' && !hideAllOption) {
    selectItemElements.push(
      <SelectItem key='all' value='all' textValue={tCommon('Todos')}>
        <div className='flex justify-between items-center'>
          <span>
            {Array.isArray(selectedFleet) && selectedFleet.length > 0
              ? tCommon('Deseleccionar todos')
              : tCommon('Seleccionar todos')}
          </span>
        </div>
      </SelectItem>
    );
  }

  // Mapping from codes to translations
  const getFleetLabel = (code: string): string => {
    switch (code) {
      case 'AIR':
        return tMarket('Aéreo');
      case 'OCEAN':
        return tMarket('Marítimo');
      case 'GROUND':
        return tMarket('Terrestre');
      default:
        return code;
    }
  };

  // Add fleet options
  fleets.forEach((fleetCode) => {
    const label = getFleetLabel(fleetCode);
    selectItemElements.push(
      <SelectItem key={fleetCode} value={fleetCode} textValue={label}>
        {label}
      </SelectItem>
    );
  });

  return (
    <Select
      label={finalLabel}
      selectedKeys={
        selectionMode === 'multiple'
          ? Array.isArray(selectedFleet)
            ? new Set(selectedFleet)
            : new Set([])
          : new Set([selectedFleet as string])
      }
      selectionMode={selectionMode}
      defaultSelectedKeys={defaultSelectedKeys && new Set(defaultSelectedKeys)}
      className={className}
      size={size}
      aria-label={`${tCommon('Selecciona tipo de flete')}`}
      renderValue={(items: SelectedItems<any>): ReactNode => {
        if (
          Array.isArray(selectedFleet) &&
          selectedFleet.length === fleets.length
        ) {
          return (
            <div className='flex items-center gap-2'>{tCommon('Todos los tipos de flete')}</div>
          );
        }
        if (Array.isArray(selectedFleet) && selectedFleet.length > 0) {
          return (
            <div className='flex items-center gap-2'>
              {selectedFleet.map(code => getFleetLabel(code)).join(', ')}
            </div>
          );
        }
        return items as ReactNode;
      }}
      onSelectionChange={handleSelectionChange}
    >
      {selectItemElements}
    </Select>
  );
};

export default React.memo(FleetFilter);
